import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';

const PlayerContext = createContext(null);

export function PlayerProvider({ children }) {
  const [isActive, setIsActive] = useState(false);
  const [mode, setMode] = useState('inline'); // 'inline' | 'mini'
  const [videoId, setVideoId] = useState(null);
  const [minimizeSource, setMinimizeSource] = useState(null); // 'drag' | 'button' | 'visibility' | 'navigation' | null
  const [shouldPlayOnLoad, setShouldPlayOnLoad] = useState(false); // user click intent to play next load

  const anchorElRef = useRef(null);
  const [anchorElState, setAnchorElState] = useState(null);
  const miniTimeoutRef = useRef(null);

  const attachAnchor = useCallback((el) => {
    if (!el) return;
    // cancel any pending minimize due to previous detach
    if (miniTimeoutRef.current) {
      clearTimeout(miniTimeoutRef.current);
      miniTimeoutRef.current = null;
    }
    anchorElRef.current = el;
    setAnchorElState(el);
    // If we arrive on a page with an anchor while mini, auto-restore inline
    if (isActive && mode === 'mini') {
      setMode('inline');
    }
  }, [isActive, mode]);

  const detachAnchor = useCallback((el) => {
    if (anchorElRef.current === el) {
      anchorElRef.current = null;
      setAnchorElState(null);
      // If active and we lose anchor while inline, schedule a debounce minimize
      if (isActive && mode === 'inline') {
        if (miniTimeoutRef.current) clearTimeout(miniTimeoutRef.current);
        miniTimeoutRef.current = setTimeout(() => {
          setMode('mini');
          setMinimizeSource('navigation');
          miniTimeoutRef.current = null;
        }, 150);
      }
    }
  }, [isActive, mode]);

  const load = useCallback((id, opts = {}) => {
    if (!id) return;
    
    // If trying to load the same video that's already active, just restore it
    if (isActive && videoId === id) {
      // If mini, restore to inline if we have an anchor
      if (mode === 'mini' && anchorElRef.current) {
        setMode('inline');
      }
      // Preserve play intent if specified
      if (opts.play) setShouldPlayOnLoad(true);
      return;
    }
    
    // Simple approach: just update the videoId and ensure player is active
    // The GlobalVideoOverlay will handle loading the new video via loadVideoById
    setVideoId(id);
    setIsActive(true);
    // Do not force inline here; attachAnchor() will restore to inline when anchor is ready
    setMinimizeSource(null);
    if (opts.play) setShouldPlayOnLoad(true);
  }, [isActive, videoId, mode]);

  const minimize = useCallback((source = 'button') => {
    setMode('mini');
    setMinimizeSource(source);
  }, []);

  const restore = useCallback(() => {
    // Only restore if we have a place to restore to
    if (anchorElRef.current) setMode('inline');
  }, []);

  const close = useCallback(() => {
    setIsActive(false);
    setVideoId(null);
    setMinimizeSource(null);
    setMode('inline'); // Reset mode to inline for next video
    setShouldPlayOnLoad(false);
  }, []);

  // Auto-minimize when app is backgrounded (iOS safe: no display:none used anywhere)
  useEffect(() => {
    const onVisibility = () => {
      if (document.visibilityState !== 'visible' && isActive) {
        setMode('mini');
        setMinimizeSource('visibility');
      }
    };
    document.addEventListener('visibilitychange', onVisibility);
    return () => document.removeEventListener('visibilitychange', onVisibility);
  }, [isActive]);

  // If inline but anchor disappears (e.g., route change), slide to mini
  useEffect(() => {
    if (!isActive) return;
    if (mode === 'inline' && !anchorElRef.current) {
      setMode('mini');
      setMinimizeSource((s) => s ?? 'navigation');
    }
  }, [isActive, mode]);

  const consumePlayIntent = useCallback(() => {
    const v = shouldPlayOnLoad;
    if (v) setShouldPlayOnLoad(false);
    return v;
  }, [shouldPlayOnLoad]);

  const value = useMemo(() => ({
    // state
    isActive,
    mode,
    videoId,
    anchorEl: anchorElState,
    minimizeSource,
    shouldPlayOnLoad,
    // actions
    attachAnchor,
    detachAnchor,
    load,
    minimize,
    restore,
    close,
    // utils
    consumePlayIntent,
  }), [isActive, mode, videoId, anchorElState, minimizeSource, shouldPlayOnLoad, attachAnchor, detachAnchor, load, minimize, restore, close, consumePlayIntent]);

  return (
    <PlayerContext.Provider value={value}>{children}</PlayerContext.Provider>
  );
}

export function usePlayer() {
  const ctx = useContext(PlayerContext);
  if (!ctx) throw new Error('usePlayer must be used within a PlayerProvider');
  return ctx;
}
